import React, { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Box, Edit, Loader2 } from '@/components/ui/lucide-memo';
import { toast } from 'sonner';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';

const translations = {
  en: {
    uploadingThumbnail: 'Uploading unit thumbnail...',
    uploadFailed: 'Upload failed',
    thumbnailUpdated: 'Thumbnail updated',
    uploadThumbnailFailed: 'Failed to upload thumbnail',
  },
  fr: {
    uploadingThumbnail: 'Envoi de la vignette...',
    uploadFailed: 'Echec envoi',
    thumbnailUpdated: 'Vignette mise a jour',
    uploadThumbnailFailed: 'Echec envoi vignette',
  },
};

interface UnitThumbnailProps {
  unitId: string;
  isEditable: boolean;
  className?: string;
  highlighted?: boolean;
  onUploaded?: () => void;
}

export const UnitThumbnail = React.memo(
  ({
    unitId,
    isEditable,
    className,
    highlighted,
    onUploaded,
  }: UnitThumbnailProps) => {
    const { kernel } = useKernel();
    const lang = useKernelStore((state) => state.language);
    const unitIdentifier = useKernelStore((state) => state.inventory.Unit.registry[unitId]?.unitIdentifier);
    const thumbnailFileId = useKernelStore((state) => state.inventory.Unit.registry[unitId]?.thumbnailFileId);
    const t = translations[lang];
    const [isUploading, setIsUploading] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleUpload = useCallback(
      async (file: File) => {
        setIsUploading(true);
        const toastId = toast.loading(t.uploadingThumbnail);
        try {
          const formData = new FormData();
          formData.append('file', file);
          const response = await fetch(`/api/inventory/units/${unitId}/thumbnail`, { method: 'POST', body: formData });
          if (!response.ok) throw new Error(t.uploadFailed);
          toast.success(t.thumbnailUpdated);
          await kernel.current?.refreshInventory('Unit');
          onUploaded?.();
        } catch {
          toast.error(t.uploadThumbnailFailed);
        } finally {
          setIsUploading(false);
          toast.dismiss(toastId);
          if (fileInputRef.current) fileInputRef.current.value = '';
        }
      },
      [unitId, kernel, onUploaded, t]
    );

    const handleContainerClick = useCallback(
      (e: React.MouseEvent) => {
        if (!isEditable || isUploading) return;
        e.stopPropagation();
        fileInputRef.current?.click();
      },
      [isEditable, isUploading]
    );

    const handleMouseEnter = useCallback(() => setIsHovered(true), []);
    const handleMouseLeave = useCallback(() => setIsHovered(false), []);

    const handleFileInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) void handleUpload(file);
      },
      [handleUpload]
    );

    useEffect(() => {
      if (!isEditable || !isHovered || isUploading) return;
      const handlePaste = (e: ClipboardEvent) => {
        const items = e.clipboardData?.items;
        if (!items) return;
        for (let i = 0; i < items.length; i++) {
          if (items[i]?.type.indexOf('image') !== -1) {
            const file = items[i]?.getAsFile();
            if (file) {
              e.preventDefault();
              e.stopPropagation();
              void handleUpload(file);
              break;
            }
          }
        }
      };
      document.addEventListener('paste', handlePaste);
      return () => document.removeEventListener('paste', handlePaste);
    }, [isEditable, isHovered, isUploading, handleUpload]);

    const imageUrl = thumbnailFileId ? `/api/inventory/assets/${thumbnailFileId}` : null;

    return (
      <div
        className={clsx(
          'bg-white/5 rounded-lg flex-none flex items-center justify-center text-white/20 overflow-hidden relative group/thumb',
          isEditable && 'cursor-pointer hover:bg-white/10 transition-colors',
          highlighted && 'animate-pulse-strong ring-2 ring-[#4DAB9A]/50 ring-offset-2 ring-offset-black',
          className || 'w-48 h-32'
        )}
        onClick={handleContainerClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          accept="image/*"
          onChange={handleFileInputChange}
        />
        {isUploading && (
          <div className="absolute inset-0 z-20 bg-black/60 flex items-center justify-center">
            <Loader2 size={20} className="text-white animate-spin" />
          </div>
        )}
        {isEditable && !isUploading && (
          <div className="absolute inset-0 z-10 bg-black/40 opacity-0 group-hover/thumb:opacity-100 transition-opacity flex items-center justify-center">
            <Edit size={16} className="text-white" />
          </div>
        )}
        {imageUrl ? (
          <img src={imageUrl} alt={unitIdentifier || unitId} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <Box size={32} />
        )}
      </div>
    );
  }
);

UnitThumbnail.displayName = 'UnitThumbnail';
